import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import Button from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';
import { FiMail, FiLock, FiAlertCircle, FiArrowLeft } from 'react-icons/fi';

const Auth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { user, signIn, signUp } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/dashboard';

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate(from, { replace: true });
    }
  }, [user, navigate, from]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { error: authError } = isLogin 
        ? await signIn(email, password)
        : await signUp(email, password);

      if (authError) throw authError;
      // On success, the useEffect above will trigger the redirect
    } catch (err) {
      setError(err.message || 'An error occurred during authentication.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyles = "w-full bg-[#0a0500]/60 border border-orange-500/20 rounded-xl pl-11 pr-4 py-3.5 text-zinc-200 placeholder-zinc-600 focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/50 transition-all outline-none shadow-inner";

  return (
    <div className="min-h-screen bg-[#0a0500] flex flex-col items-center justify-center p-6 relative overflow-hidden selection:bg-orange-500/30 selection:text-amber-100">
      
      {/* Background Effects */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] bg-orange-600/15 blur-[120px] rounded-full pointer-events-none"></div>
      
      {/* Back to Home */}
      <Link to="/" className="absolute top-8 left-8 text-zinc-500 hover:text-amber-100 transition-colors flex items-center gap-2 text-sm font-medium z-20">
        <SafeIcon icon={FiArrowLeft} /> Back to Home
      </Link>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md relative z-10"
      >
        <div className="p-8 sm:p-10 rounded-3xl bg-zinc-900/40 backdrop-blur-xl border border-orange-500/20 shadow-[0_0_50px_rgba(234,88,12,0.1)]">
          
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500/20 to-yellow-500/20 border border-orange-500/30 mb-6 shadow-[0_0_20px_rgba(234,88,12,0.2)]">
              <img src="https://whatastory.b-cdn.net/tools/momentoftruth/logo.svg" alt="Logo" className="w-8 h-8 drop-shadow-[0_0_10px_rgba(234,88,12,0.5)]" />
            </div>
            <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-100 via-orange-200 to-amber-50 mb-2">
              {isLogin ? 'Welcome Back' : 'Create Account'}
            </h2>
            <p className="text-zinc-400 text-sm">
              {isLogin ? 'Enter your credentials to access your workspace.' : 'Join the premium enterprise social proof platform.'}
            </p>
          </div>

          <AnimatePresence mode="wait">
            {error && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20 flex items-start gap-3"
              >
                <SafeIcon icon={FiAlertCircle} className="text-red-400 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-200/90 leading-relaxed">{error}</p>
              </motion.div>
            )}
          </AnimatePresence>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-4">
              <div className="relative">
                <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500 text-lg" />
                <input
                  type="email"
                  required
                  placeholder="name@company.com"
                  className={inputStyles}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="relative">
                <SafeIcon icon={FiLock} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500 text-lg" />
                <input
                  type="password"
                  required
                  placeholder="••••••••"
                  className={inputStyles}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={6}
                />
              </div>
            </div>

            <Button 
              type="submit" 
              variant="primary" 
              className="w-full !py-3.5 text-md shadow-[0_0_30px_rgba(234,88,12,0.25)]"
              disabled={loading}
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                isLogin ? 'Sign In' : 'Create Account'
              )}
            </Button>
          </form>

          <div className="mt-8 text-center">
            <button
              type="button"
              onClick={() => {
                setIsLogin(!isLogin);
                setError(null);
              }}
              className="text-sm text-zinc-400 hover:text-amber-100 transition-colors duration-300"
            >
              {isLogin ? "Don't have an account? " : "Already have an account? "}
              <span className="text-orange-400 font-medium hover:underline underline-offset-4">
                {isLogin ? 'Create one' : 'Sign in'}
              </span>
            </button>
          </div>
          
        </div>
      </motion.div>
    </div>
  );
};

export default Auth;